import {
  createAppSelector,
  selectCurrentCourseId,
  selectPageGroupUsers,
  selectPageGroups,
  selectPages,
} from "./base";
import { selectEnabledCourseUsers } from "./section-user";
import type { ExtendedPageGroupUserType, NestedPageGroupType } from "./types";

export const selectPageGroupsByPageId = createAppSelector(
  [(state, pageId: string) => pageId, selectPageGroups],
  (pageId, pageGroups) => pageGroups.filter((page_group) => page_group.page_id === pageId),
);

export const selectPageGroupUsersByPageId = createAppSelector(
  [
    (state, pageId: string) => pageId,
    selectPageGroupUsers,
    (state) => state.page_groups,
    selectEnabledCourseUsers,
    (state) => state.users,
  ],
  (
    pageId,
    pageGroupUsers,
    pageGroupMap,
    enabledCourseUsers,
    userMap,
  ): ExtendedPageGroupUserType[] => {
    return pageGroupUsers.flatMap((pgu) => {
      const group = pageGroupMap[pgu.page_group_id];
      if (!group || group.page_id !== pageId) {
        return [];
      }

      const enabledCourseUser = enabledCourseUsers.find((cu) => cu.id === pgu.section_user_id);
      if (!enabledCourseUser) {
        return [];
      }

      const user = userMap[enabledCourseUser.user_id];
      if (!user) {
        return [];
      }

      return [
        {
          ...pgu,
          user_id: user.id,
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
        },
      ];
    });
  },
);

export const selectPageGroupsWithUsersByPageId = createAppSelector(
  [selectPageGroupsByPageId, selectPageGroupUsersByPageId],
  (pageGroups, pageGroupUsers) =>
    pageGroups.map((page_group): NestedPageGroupType => {
      const pageGroupUsersForPageGroup = pageGroupUsers.filter(
        (pgu) => pgu.page_group_id === page_group.id,
      );
      return {
        ...page_group,
        page_group_users: pageGroupUsersForPageGroup,
      };
    }),
);

export const selectPageGroupForReadyUser = createAppSelector(
  [
    (state, pageId: string) => pageId,
    (state, pageId: string, userId: string) => userId,
    selectPageGroups,
    selectPageGroupUsers,
    selectEnabledCourseUsers,
  ],
  (pageId, userId, page_groups, page_group_users, course_users) => {
    if (!pageId || !userId) {
      return null;
    }

    const courseUserIds = course_users
      .filter((course_user) => course_user.user_id === userId)
      .map((course_user) => course_user.id);

    // Get a list of the user's page group ids
    const userGroupIds = page_group_users
      .filter(
        (group_user) => courseUserIds.includes(group_user.section_user_id) && group_user.is_ready,
      )
      .map((group_user) => group_user.page_group_id);

    // Go through the groups and find the group for the given page and user
    return page_groups.find(
      (page_group) => page_group.page_id === pageId && userGroupIds.includes(page_group.id),
    );
  },
);

export const selectReadiedUsersInPageGroup = createAppSelector(
  [
    (state, pageGroupId: string) => pageGroupId,
    selectPageGroupUsers,
    selectEnabledCourseUsers,
    (state) => state.users,
  ],
  (pageGroupId, page_group_users, course_users, users) => {
    if (!pageGroupId) {
      return [];
    }

    // Get a list of the course_user's for this group
    const courseUserIds = page_group_users
      .filter((group_user) => group_user.page_group_id === pageGroupId && group_user.is_ready)
      .map((group_user) => group_user.section_user_id);

    // Get a list of all users that match a course_user.user_id
    return course_users
      .filter((course_user) => courseUserIds.includes(course_user.id))
      .map((course_user) => {
        const user = users[course_user.user_id];
        return {
          ...user,
        };
      });
  },
);

export const selectPageGroupsForCurrentCourse = createAppSelector(
  [selectPageGroups, selectPages, selectCurrentCourseId],
  (pageGroups, pages, currentCourseId) => {
    const pagesForCurrentCourse = pages.filter((page) => page.section_id === currentCourseId);
    const pageIds = new Set(pagesForCurrentCourse.map((page) => page.id));
    return pageGroups.filter((pageGroup) => pageIds.has(pageGroup.page_id));
  },
);

export const selectReleasedPageGroupsForCurrentCourse = createAppSelector(
  [selectPageGroups, selectPages, selectCurrentCourseId],
  (pageGroups, pages, currentCourseId) => {
    const releasePagesForCurrentCourse = pages.filter(
      (page) => page.section_id === currentCourseId && page.released_at,
    );
    const pageIds = new Set(releasePagesForCurrentCourse.map((page) => page.id));
    return pageGroups.filter((pageGroup) => pageIds.has(pageGroup.page_id));
  },
);

export const selectPageGroupUsersForCurrentCourse = createAppSelector(
  [selectPageGroupUsers, selectReleasedPageGroupsForCurrentCourse],
  (pageGroupUsers, pageGroups) => {
    const pageGroupIds = new Set(pageGroups.map((pageGroup) => pageGroup.id));
    return pageGroupUsers.filter((pageGroupUser) => pageGroupIds.has(pageGroupUser.page_group_id));
  },
);
